import React, { useState, useEffect, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Badge } from '../../components/ui/badge';
import { 
  Wallet, 
  Search, 
  AlertCircle, 
  CheckCircle, 
  ExternalLink, 
  Loader2,
  Calendar,
  DollarSign,
  FileText,
  WifiOff,
  User,
  Clock,
  CreditCard,
  Info,
  AlertTriangle,
  Download,
  FileDown,
  ChevronDown,
  ChevronUp,
  Copy,
  MessageSquare,
  Users
} from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import axios from 'axios';
import { tituloDeuda, boletaConDescuento, leyendaDeudaTotal, desgloseDeudaTexto } from '../../utils/deudaUtils';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

// URLs de los formularios de adhesión a débito automático
const FORMULARIO_DEBITO_DIRECTO = "https://customer-assets.emergentagent.com/job_95809ea8-41a4-4c57-bf50-f6d22700a8cc/artifacts/0fjp4v8g_F.-Solicitud-Debito-Directo.pdf";
const FORMULARIO_DEBITO_VISA = "https://customer-assets.emergentagent.com/job_95809ea8-41a4-4c57-bf50-f6d22700a8cc/artifacts/98tt2yw9_F.-Debito-automatico-CREDITO-VISA.pdf";

export default function AdminConsultaDeudaPage() {
  const { token } = useAuth();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [estado, setEstado] = useState(null);
  const [adhesionExpanded, setAdhesionExpanded] = useState(false);
  const [planesPagoExpanded, setPlanesPagoExpanded] = useState(false);
  const [deudaExpanded, setDeudaExpanded] = useState(false);
  const [pendienteExpanded, setPendienteExpanded] = useState(false);
  const [copiado, setCopiado] = useState(false);
  
  const [matricula, setMatricula] = useState('');
  const [documento, setDocumento] = useState('');
  const [fechaNacimiento, setFechaNacimiento] = useState('');
  
  // Estados para el desplegable de usuarios
  const [usuariosRegistrados, setUsuariosRegistrados] = useState([]);
  const [loadingUsuarios, setLoadingUsuarios] = useState(false);
  const [busquedaUsuario, setBusquedaUsuario] = useState('');
  const [usuarioSeleccionado, setUsuarioSeleccionado] = useState(null);
  const [mostrarDesplegable, setMostrarDesplegable] = useState(false);

  // Cargar usuarios registrados al montar
  useEffect(() => {
    cargarUsuariosRegistrados();
  }, []);

  // Deep-link: prefill desde query params (usado por la Bandeja Unificada).
  // Ej: /admin/consulta-deuda?matricula=1234&documento=99999999&fecha_nacimiento=1980-05-10
  const [urlParams] = useSearchParams();
  const autoSubmittedRef = useRef(false);
  useEffect(() => {
    const m = urlParams.get('matricula');
    const d = urlParams.get('documento');
    const f = urlParams.get('fecha_nacimiento');
    if (m) setMatricula(m);
    if (d) setDocumento(d);
    if (f) setFechaNacimiento(f);
    // Auto-submit si vienen los 3 y el token está disponible.
    if (m && d && f && token && !autoSubmittedRef.current) {
      autoSubmittedRef.current = true;
      setLoading(true);
      setError(null);
      setEstado(null);
      axios.post(
        `${API_URL}/antuar/consultar-matriculado`,
        { matricula: m, documento: d, fecha_nacimiento: f },
        { headers: { Authorization: `Bearer ${token}` } }
      )
        .then(res => setEstado(res.data))
        .catch(err => setError(err.response?.data?.detail || 'Error al consultar'))
        .finally(() => setLoading(false));
    }
  }, [urlParams, token]);

  const cargarUsuariosRegistrados = async (buscar = '') => {
    try {
      setLoadingUsuarios(true);
      const res = await axios.get(`${API_URL}/antuar/usuarios-registrados`, {
        params: { buscar },
        headers: { Authorization: `Bearer ${token}` }
      });
      setUsuariosRegistrados(res.data.usuarios || []);
    } catch (err) {
      console.error('Error cargando usuarios:', err);
    } finally {
      setLoadingUsuarios(false);
    }
  };

  const handleBusquedaUsuario = (valor) => {
    setBusquedaUsuario(valor);
    if (valor.length >= 2) {
      cargarUsuariosRegistrados(valor);
    } else if (valor.length === 0) {
      cargarUsuariosRegistrados();
    }
    setMostrarDesplegable(true);
  };

  const seleccionarUsuario = (usuario) => {
    setUsuarioSeleccionado(usuario);
    setMatricula(usuario.matricula || '');
    setDocumento(usuario.documento || '');
    setFechaNacimiento(usuario.fecha_nacimiento || '');
    setBusquedaUsuario(usuario.nombre_completo);
    setMostrarDesplegable(false);
  };

  const limpiarSeleccion = () => {
    setUsuarioSeleccionado(null);
    setMatricula('');
    setDocumento('');
    setFechaNacimiento('');
    setBusquedaUsuario('');
    setEstado(null);
  };

  // Generar texto para WhatsApp
  const generarTextoWhatsApp = () => {
    if (!estado) return '';
    
    const fechaHoy = new Date().toLocaleDateString('es-AR', { timeZone: 'America/Argentina/Buenos_Aires', day: '2-digit', 
      month: '2-digit', 
      year: 'numeric' 
    });
    
    let texto = `📋 *Estado de cuenta al ${fechaHoy}*\n\n`;
    
    // Caso: SIN DEUDA VENCIDA
    if (estado.total_deuda_vencida === 0) {
      texto += `✅ *¡Estás al día!*\n`;
      texto += `No tenés cuotas vencidas.\n`;
      
      if (estado.total_pendiente > 0) {
        texto += `\n📅 *Próxima cuota:* $${estado.total_pendiente.toLocaleString('es-AR', { timeZone: 'America/Argentina/Buenos_Aires', minimumFractionDigits: 2 })}\n`;
      }
      
      return texto;
    }
    
    // Caso: CON DEUDA VENCIDA
    const money = (n) => (n || 0).toLocaleString('es-AR', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
    const { total, nota, lineas } = desgloseDeudaTexto(estado);
    texto += `💰 *Deuda total: $${money(total)}*\n`;
    if (nota) texto += `_${nota}_\n`;
    lineas.forEach(ln => { texto += ln.startsWith('Concepto') ? `${ln}\n` : `• ${ln}\n`; });
    
    // Opción de pago en efectivo (boleta pago total)
    if (estado.boleta_pago_total?.tiene_boleta && estado.boleta_pago_total?.vigente) {
      const importeBoleta = estado.boleta_pago_total.importe.toLocaleString('es-AR', { timeZone: 'America/Argentina/Buenos_Aires', minimumFractionDigits: 2 });
      texto += `\n💵 *Opción Pago en Efectivo/Transferencia:* $${importeBoleta}\n`;
      if (boletaConDescuento(estado)) texto += `_(Incluye: Deuda anterior + mes en curso con 80% de descuento en intereses por mora)_\n`;
      texto += `_Vence: ${estado.boleta_pago_total.vencimiento}_\n`;
    }
    
    // Agregar info de planes de pago si están disponibles
    if (estado.planes_pago?.disponible && estado.planes_pago?.opciones?.length > 0) {
      const opciones = estado.planes_pago.opciones;
      const maxCuotas = Math.max(...opciones.map(o => o.cantidad_cuotas));
      
      // Opción de pago al contado (si no se mostró arriba con boleta_pago_total)
      if (estado.planes_pago.pago_total && !estado.boleta_pago_total?.tiene_boleta) {
        const pagoTotal = estado.planes_pago.pago_total.toLocaleString('es-AR', { timeZone: 'America/Argentina/Buenos_Aires', minimumFractionDigits: 2 });
        texto += `\n💵 *Opción Pago al Contado:* $${pagoTotal}\n`;
        texto += `_(Deuda anterior + mes en curso con 80% de descuento en intereses por mora)_\n`;
      }
      
      texto += `\n📝 *Opciones de Plan de Pagos (hasta ${maxCuotas} cuotas):*\n\n`;
      
      // Agrupar por rango de cuotas
      const rango2a3 = opciones.filter(o => o.cantidad_cuotas >= 2 && o.cantidad_cuotas <= 3);
      const rango4a7 = opciones.filter(o => o.cantidad_cuotas >= 4 && o.cantidad_cuotas <= 7);
      const rango8a12 = opciones.filter(o => o.cantidad_cuotas >= 8 && o.cantidad_cuotas <= 12);
      
      // 2 a 3 cuotas - 80% desc, SIN interés
      if (rango2a3.length > 0) {
        const detalles = rango2a3.map(o => 
          `${o.cantidad_cuotas} cuotas de $${o.importe_cuota.toLocaleString('es-AR', { timeZone: 'America/Argentina/Buenos_Aires', minimumFractionDigits: 2 })}`
        ).join(' o ');
        texto += `✅ *2 a 3 cuotas* _(80% desc. en mora, SIN interés)_\n`;
        texto += `     ${detalles}\n\n`;
      }
      
      // 4 a 7 cuotas - 50% desc, CON interés
      if (rango4a7.length > 0) {
        const detalles = rango4a7.map(o => 
          `${o.cantidad_cuotas} cuotas de $${o.importe_cuota.toLocaleString('es-AR', { timeZone: 'America/Argentina/Buenos_Aires', minimumFractionDigits: 2 })}`
        ).join(', ');
        texto += `🔸 *4 a 7 cuotas* _(50% desc. en mora, CON interés)_\n`;
        texto += `     ${detalles}\n\n`;
      }
      
      // 8 a 12 cuotas - 20% desc, CON interés
      if (rango8a12.length > 0) {
        const detalles = rango8a12.map(o => 
          `${o.cantidad_cuotas} cuotas de $${o.importe_cuota.toLocaleString('es-AR', { timeZone: 'America/Argentina/Buenos_Aires', minimumFractionDigits: 2 })}`
        ).join(', ');
        texto += `🔹 *8 a 12 cuotas* _(20% desc. en mora, CON interés)_\n`;
        texto += `     ${detalles}\n\n`;
      }
      
      texto += `🔗 *¿Cómo gestionar el plan por autogestión?*\n`;
      texto += `Podés hacerlo vos directamente ingresando al Portal ANTUAR: https://www.antuar.com.ar/catspba\n`;
      texto += `_(te va a pedir N° Matrícula, DNI y Fecha de Nacimiento)_ > Opción Plan de Pagos\n\n`;
      texto += `💳 _El plan por autogestión requiere adhesión con Tarjeta de Crédito VISA para el cobro de la cuota de moratoria + matrícula mensual._\n\n`;
      texto += `📲 *¿Querés pagar por otros medios?*\n`;
      texto += `Si preferís gestionar un plan de pago abonando por transferencia, efectivo o Mercado Pago, solicitalo vía WhatsApp al 11-4098-9757 o por mail a info@colegiotslomas.org\n\n`;
      texto += `⚠️ _Los montos informados tienen validez hasta el vencimiento de la cuota del mes en curso. Pasada esa fecha, se actualizarán con los intereses correspondientes._`;
    }
    
    // Si tiene plan de pago vigente, agregar aclaración al final
    if (estado.planes_pago?.tiene_plan_vigente) {
      texto += `\n\n⚠️ *Importante:* El sistema detecta que tenés un plan de pagos vigente. Si querés gestionar un nuevo plan de pagos, informamelo por acá y te digo cómo seguimos.`;
    }
    
    return texto;
  };

  const copiarTexto = () => {
    const texto = generarTextoWhatsApp();
    navigator.clipboard.writeText(texto).then(() => {
      setCopiado(true);
      setTimeout(() => setCopiado(false), 2000);
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError(null);
    setEstado(null);
    
    try {
      const response = await axios.post(
        `${API_URL}/antuar/consultar-matriculado`,
        {
          matricula,
          documento,
          fecha_nacimiento: fechaNacimiento
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setEstado(response.data);
    } catch (err) {
      setError(err.response?.data?.detail || 'Error al consultar');
    } finally {
      setLoading(false);
    }
  };

  // Separar cuotas vencidas (incluye multas) y por vencer
  const cuotasVencidas = estado?.cuotas_vencidas?.filter(c => c.es_cuota_mensual || c.es_multa) || [];
  const cuotasPorVencer = estado?.cuotas_por_vencer?.filter(c => c.es_cuota_mensual) || [];
  
  // Cuotas de convenio de cancelación (son cuotas de plan de pago, no anticipos)
  const cuotasConvenio = estado?.cuotas_vencidas?.filter(c => c.es_convenio) || [];
  
  // Opciones de pago anticipado (pago anual/semestral con bonificación)
  const opcionesPago = estado?.cuotas_por_vencer?.filter(c => 
    !c.es_cuota_mensual && 
    !c.es_multa && 
    !c.concepto?.toLowerCase().includes('convenio') &&
    !c.periodo?.toLowerCase().includes('cuota') &&
    (c.periodo?.includes('/AA') || c.periodo?.includes('/S1') || c.periodo?.includes('/S2'))
  ) || [];

  return (
    <div className="p-6">
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-3">
          <Wallet className="w-7 h-7 text-cyan-600" />
          Consulta de Deuda CATSPBA
        </h1>
        <p className="text-gray-500 mt-1">Consultá el estado de cuenta de cualquier matriculado en ANTUAR</p>
      </div>

      {/* Formulario de búsqueda */}
      <Card className="mb-6">
        <CardHeader>
          <CardTitle className="text-lg flex items-center gap-2">
            <Search className="w-5 h-5" />
            Buscar Matriculado
          </CardTitle>
        </CardHeader>
        <CardContent>
          {/* Selector de usuario registrado */}
          <div className="mb-4 pb-4 border-b">
            <Label className="flex items-center gap-2 mb-2">
              <Users className="w-4 h-4" />
              Seleccionar usuario registrado
            </Label>
            <div className="relative">
              <Input
                placeholder="Buscar por nombre, apellido, matrícula o DNI..."
                value={busquedaUsuario}
                onChange={(e) => handleBusquedaUsuario(e.target.value)}
                onFocus={() => setMostrarDesplegable(true)}
                className="pr-10"
              />
              {usuarioSeleccionado && (
                <button
                  type="button"
                  onClick={limpiarSeleccion}
                  className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                >
                  ✕
                </button>
              )}
              
              {/* Desplegable de usuarios */}
              {mostrarDesplegable && (
                <div className="absolute z-10 w-full mt-1 bg-white border rounded-lg shadow-lg max-h-60 overflow-y-auto">
                  {loadingUsuarios ? (
                    <div className="p-3 text-center text-gray-500">
                      <Loader2 className="w-4 h-4 animate-spin inline mr-2" />
                      Cargando...
                    </div>
                  ) : usuariosRegistrados.length === 0 ? (
                    <div className="p-3 text-center text-gray-500">
                      {busquedaUsuario ? 'No se encontraron usuarios' : 'Escribí para buscar'}
                    </div>
                  ) : (
                    usuariosRegistrados.map((usuario) => (
                      <button
                        key={usuario.id}
                        type="button"
                        onClick={() => seleccionarUsuario(usuario)}
                        className="w-full p-3 text-left hover:bg-gray-50 border-b last:border-b-0 flex items-center justify-between"
                      >
                        <div>
                          <p className="font-medium text-gray-900">{usuario.nombre_completo}</p>
                          <p className="text-sm text-gray-500">
                            Mat. {usuario.matricula} · DNI {usuario.documento}
                          </p>
                        </div>
                        <CheckCircle className={`w-5 h-5 ${usuarioSeleccionado?.id === usuario.id ? 'text-green-500' : 'text-transparent'}`} />
                      </button>
                    ))
                  )}
                </div>
              )}
            </div>
            {usuarioSeleccionado && (
              <p className="text-sm text-green-600 mt-2 flex items-center gap-1">
                <CheckCircle className="w-4 h-4" />
                Datos completados automáticamente
              </p>
            )}
          </div>

          {/* Campos manuales (se completan automáticamente si selecciona usuario) */}
          <form onSubmit={handleSubmit} className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <div>
              <Label htmlFor="matricula">Matrícula</Label>
              <Input
                id="matricula"
                placeholder="Ej: 13509"
                value={matricula}
                onChange={(e) => setMatricula(e.target.value)}
                required
              />
            </div>
            <div>
              <Label htmlFor="documento">DNI</Label>
              <Input
                id="documento"
                placeholder="Ej: 29909564"
                value={documento}
                onChange={(e) => setDocumento(e.target.value)}
                required
              />
            </div>
            <div>
              <Label htmlFor="fecha">Fecha de Nacimiento</Label>
              <Input
                id="fecha"
                type="date"
                value={fechaNacimiento}
                onChange={(e) => setFechaNacimiento(e.target.value)}
                required
              />
            </div>
            <div className="flex items-end">
              <Button type="submit" className="w-full" disabled={loading}>
                {loading ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Consultando...
                  </>
                ) : (
                  <>
                    <Search className="w-4 h-4 mr-2" />
                    Consultar legajo
                  </>
                )}
              </Button>
            </div>
          </form>
        </CardContent>
      </Card>

      {/* Error */}
      {error && (
        <Card className="border-red-200 mb-6">
          <CardContent className="py-6">
            <div className="flex items-center gap-3 text-red-600">
              <AlertCircle className="w-6 h-6" />
              <p>{error}</p>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Resultado - Error de conexión */}
      {estado && !estado.success && (
        <Card className="border-red-200">
          <CardContent className="py-8 text-center">
            <WifiOff className="w-12 h-12 text-red-400 mx-auto mb-4" />
            <h3 className="text-lg font-semibold text-gray-900 mb-2">No se pudo consultar</h3>
            <p className="text-gray-600">{estado.error}</p>
          </CardContent>
        </Card>
      )}

      {estado?.success && (
        <>
          {/* 1. DATOS DEL MATRICULADO */}
          <Card className="mb-4">
            <CardContent className="py-4">
              <div className="flex items-center justify-between flex-wrap gap-4">
                <div className="flex items-center gap-3">
                  <div className="w-12 h-12 bg-cyan-100 rounded-full flex items-center justify-center">
                    <User className="w-6 h-6 text-cyan-600" />
                  </div>
                  <div>
                    <p className="font-semibold text-gray-900">
                      {estado.nombre || `Matrícula ${estado.matricula}`}
                    </p>
                    {estado.nombre && (
                      <p className="text-sm text-gray-500">Matrícula: {estado.matricula}</p>
                    )}
                    {!estado.nombre && (
                      <p className="text-xs text-gray-400 italic">Nombre no disponible en ANTUAR</p>
                    )}
                  </div>
                </div>
                <div className="flex items-center gap-2 flex-wrap">
                  {estado.fecha_consulta && (
                    <Badge variant="outline" className="text-gray-500">
                      <Clock className="w-3 h-3 mr-1" />
                      Consulta: {estado.fecha_consulta}
                    </Badge>
                  )}
                  {estado.tiene_plan_pago && (
                    <Badge className="bg-blue-100 text-blue-700">
                      <FileText className="w-3 h-3 mr-1" />
                      Plan de pago activo
                    </Badge>
                  )}
                </div>
              </div>
            </CardContent>
          </Card>

          {/* 2. INFORMACIÓN SOBRE DÉBITO AUTOMÁTICO */}
          <Card className="mb-4 border-blue-200 bg-blue-50">
            <CardHeader className="py-3">
              <CardTitle className="text-sm flex items-center gap-2 text-blue-800">
                <CreditCard className="w-4 h-4" />
                Información sobre Débito Automático
              </CardTitle>
            </CardHeader>
            <CardContent className="pt-0 space-y-2">
              <div className="flex gap-2 p-2 bg-white rounded-lg border border-blue-100">
                <Info className="w-4 h-4 text-blue-600 flex-shrink-0 mt-0.5" />
                <div>
                  <p className="font-medium text-gray-900 text-xs">Débito con Tarjeta de DÉBITO</p>
                  <p className="text-xs text-gray-600">
                    Los débitos se realizan <strong>entre el 6 y el 12 de cada mes</strong>. 
                    Si tenés adhesión, y te figura pendiente de pago es posible que el pago aún no haya impactado.
                  </p>
                </div>
              </div>
              
              <div className="flex gap-2 p-2 bg-white rounded-lg border border-blue-100">
                <Info className="w-4 h-4 text-blue-600 flex-shrink-0 mt-0.5" />
                <div>
                  <p className="font-medium text-gray-900 text-xs">Débito con Tarjeta de CRÉDITO VISA</p>
                  <p className="text-xs text-gray-600">
                    El cobro se realiza <strong>entre 48 y 72 horas antes de la fecha de vencimiento</strong>. 
                    Si te figura pendiente de pago antes del vencimiento, probablemente sea por esto. Volvé a verificar posterior a la fecha de vencimiento.
                  </p>
                </div>
              </div>

              {/* Acordeón de Adhesión a Débito Automático */}
              <div className="border-t border-blue-200 pt-2">
                <button
                  onClick={() => setAdhesionExpanded(!adhesionExpanded)}
                  className="w-full flex items-center justify-between p-2 bg-green-100 hover:bg-green-200 rounded-lg border border-green-300 transition-colors"
                >
                  <div className="flex items-center gap-2">
                    <FileDown className="w-4 h-4 text-green-700" />
                    <span className="font-medium text-green-800 text-xs">Adhesión a Débito Automático</span>
                  </div>
                  {adhesionExpanded ? (
                    <ChevronUp className="w-4 h-4 text-green-700" />
                  ) : (
                    <ChevronDown className="w-4 h-4 text-green-700" />
                  )}
                </button>
                
                {adhesionExpanded && (
                  <div className="mt-2 p-3 bg-green-50 rounded-lg border border-green-200">
                    <p className="text-xs text-gray-600 mb-2">
                      Podés adherir al débito automático con tu tarjeta VISA o que te lo debiten de tu cuenta bancaria 
                      completando los siguientes formularios, firmándolos y enviándolos de vuelta firmados de puño y letra.
                    </p>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                      <Button variant="outline" size="sm" asChild className="text-xs h-8">
                        <a href={FORMULARIO_DEBITO_DIRECTO} target="_blank" rel="noopener noreferrer">
                          <Download className="w-3 h-3 mr-1" />
                          Débito Directo (Cuenta)
                        </a>
                      </Button>
                      <Button variant="outline" size="sm" asChild className="text-xs h-8">
                        <a href={FORMULARIO_DEBITO_VISA} target="_blank" rel="noopener noreferrer">
                          <Download className="w-3 h-3 mr-1" />
                          Débito VISA
                        </a>
                      </Button>
                    </div>
                  </div>
                )}
              </div>
            </CardContent>
          </Card>

          {/* TEXTO PARA WHATSAPP */}
          <Card className="mb-4 border-green-200 bg-green-50">
            <CardHeader className="pb-2">
              <CardTitle className="text-sm flex items-center gap-2 text-green-800">
                <MessageSquare className="w-4 h-4" />
                Texto para WhatsApp
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="relative">
                <textarea
                  readOnly
                  value={generarTextoWhatsApp()}
                  className="w-full h-40 p-3 text-sm bg-white border border-green-200 rounded-lg resize-none focus:outline-none"
                />
                <Button
                  size="sm"
                  variant="outline"
                  className={`absolute top-2 right-2 ${copiado ? 'bg-green-100 text-green-700' : ''}`}
                  onClick={copiarTexto}
                >
                  {copiado ? (
                    <>
                      <CheckCircle className="w-3 h-3 mr-1" />
                      Copiado
                    </>
                  ) : (
                    <>
                      <Copy className="w-3 h-3 mr-1" />
                      Copiar
                    </>
                  )}
                </Button>
              </div>
            </CardContent>
          </Card>

          {/* 4. ESTADO DE CUENTA */}
          {/* Caso: SIN DEUDA VENCIDA - Mostrar que está al día */}
          {estado.total_deuda_vencida === 0 && (
            <Card className="mb-4 border-emerald-300 bg-emerald-50">
              <CardContent className="py-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-lg font-semibold text-emerald-800 mb-1">✓ Está al día</p>
                    <p className="text-sm text-emerald-700">
                      No tiene cuotas vencidas
                    </p>
                    {estado.total_pendiente > 0 && (
                      <p className="text-sm text-gray-600 mt-2">
                        Próxima cuota: <span className="font-medium">${estado.total_pendiente?.toLocaleString('es-AR', { timeZone: 'America/Argentina/Buenos_Aires', minimumFractionDigits: 2 })}</span>
                      </p>
                    )}
                  </div>
                  <div className="w-16 h-16 rounded-full flex items-center justify-center bg-emerald-100">
                    <CheckCircle className="w-8 h-8 text-emerald-600" />
                  </div>
                </div>
              </CardContent>
            </Card>
          )}

          {/* Caso: CON DEUDA VENCIDA - Mostrar deuda desplegable */}
          {estado.total_deuda_vencida > 0 && (
            <Card className="mb-4 border-red-300">
              <CardContent 
                className={`py-6 ${cuotasVencidas.length > 0 ? 'cursor-pointer hover:bg-gray-50 transition-colors' : ''}`}
                onClick={() => cuotasVencidas.length > 0 && setDeudaExpanded(!deudaExpanded)}
              >
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-gray-500 mb-1" data-testid="deuda-titulo">{tituloDeuda(estado)}</p>
                    <p className="text-3xl font-bold text-red-600" data-testid="deuda-total-monto">
                      ${estado.total_deuda_vencida?.toLocaleString('es-AR', { timeZone: 'America/Argentina/Buenos_Aires', minimumFractionDigits: 2 })}
                    </p>
                    <div className="text-sm text-gray-600 mt-1 space-y-0.5">
                      <p data-testid="deuda-matricula-monto">{estado.deuda_matricula > 0 ? <>Deuda de matrícula: <span className="font-medium text-red-600">${estado.deuda_matricula.toLocaleString('es-AR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span></> : <span className="text-green-700">Matrícula: ¡al día!</span>}</p>
                      {estado.deuda_convenio > 0 && (
                        <p data-testid="deuda-convenio-monto">Deuda de convenio: <span className="font-medium text-red-600">${(estado.deuda_convenio || 0).toLocaleString('es-AR', { timeZone: 'America/Argentina/Buenos_Aires', minimumFractionDigits: 2 })}</span></p>
                      )}
                      {estado.deuda_otros > 0 && (
                        <p data-testid="deuda-otros-monto">Otras deudas (multas): <span className="font-medium text-red-600">${estado.deuda_otros.toLocaleString('es-AR', { timeZone: 'America/Argentina/Buenos_Aires', minimumFractionDigits: 2 })}</span></p>
                      )}
                    </div>
                    {cuotasVencidas.length > 0 && (
                      <p className="text-sm text-blue-600 mt-2 flex items-center gap-1">
                        {deudaExpanded ? (
                          <>
                            <ChevronUp className="w-4 h-4" />
                            Ocultar detalle ({cuotasVencidas.length} items)
                          </>
                        ) : (
                          <>
                            <ChevronDown className="w-4 h-4" />
                            Ver detalle ({cuotasVencidas.length} items)
                          </>
                        )}
                      </p>
                    )}
                  </div>
                  <div className="w-16 h-16 rounded-full flex items-center justify-center bg-red-100">
                    <AlertTriangle className="w-8 h-8 text-red-600" />
                  </div>
                </div>
              </CardContent>
              
              {/* Detalle de cuotas vencidas - Desplegable */}
              {deudaExpanded && cuotasVencidas.length > 0 && (
                <CardContent className="pt-0 border-t">
                  <div className="space-y-2 mt-4">
                    {cuotasVencidas.map((cuota, idx) => (
                      <div key={idx} className="flex items-center justify-between p-3 bg-red-50 rounded-lg border border-red-200">
                        <div>
                          <p className="font-medium text-gray-900">
                            {cuota.concepto}{cuota.periodo && !cuota.es_multa ? ` - ${cuota.periodo}` : ''}
                          </p>
                          {!cuota.es_multa && (
                            <p className="text-sm text-red-600">
                              Venció: {cuota.vencimiento_real || cuota.vencimiento} ({cuota.dias_mora || Math.abs(cuota.dias_para_vencer)} días de mora)
                            </p>
                          )}
                          {cuota.es_multa && (
                            <p className="text-sm text-red-600">Multa - Monto fijo</p>
                          )}
                        </div>
                        <p className="font-bold text-red-700">
                          ${cuota.importe.toLocaleString('es-AR', { timeZone: 'America/Argentina/Buenos_Aires', minimumFractionDigits: 2 })}
                        </p>
                      </div>
                    ))}
                  </div>
                </CardContent>
              )}
            </Card>
          )}

          {/* 5. PENDIENTE DE PAGO (no vencido) - DESPLEGABLE */}
          {estado.total_pendiente > 0 && (
            <Card className="mb-4 border-amber-200">
              <CardContent 
                className={`py-6 ${cuotasPorVencer.length > 0 ? 'cursor-pointer hover:bg-amber-50 transition-colors' : ''}`}
                onClick={() => cuotasPorVencer.length > 0 && setPendienteExpanded(!pendienteExpanded)}
              >
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-gray-500 mb-1">Cuota del mes en curso <span className="text-xs">(no es deuda)</span></p>
                    <p className="text-2xl font-bold text-amber-600">
                      ${estado.total_pendiente?.toLocaleString('es-AR', { timeZone: 'America/Argentina/Buenos_Aires', minimumFractionDigits: 2 }) || '0,00'}
                    </p>
                    <p className="text-sm text-gray-500 mt-1">
                      Matrícula del período corriente
                    </p>
                    {cuotasPorVencer.length > 0 && (
                      <p className="text-sm text-blue-600 mt-2 flex items-center gap-1">
                        {pendienteExpanded ? (
                          <>
                            <ChevronUp className="w-4 h-4" />
                            Ocultar detalle ({cuotasPorVencer.length} cuotas)
                          </>
                        ) : (
                          <>
                            <ChevronDown className="w-4 h-4" />
                            Ver detalle ({cuotasPorVencer.length} cuotas)
                          </>
                        )}
                      </p>
                    )}
                  </div>
                  <div className="w-14 h-14 rounded-full flex items-center justify-center bg-amber-100">
                    <Calendar className="w-7 h-7 text-amber-600" />
                  </div>
                </div>
              </CardContent>
              
              {/* Detalle de cuotas por vencer - Desplegable */}
              {pendienteExpanded && cuotasPorVencer.length > 0 && (
                <CardContent className="pt-0 border-t">
                  <div className="space-y-2 mt-4">
                    {cuotasPorVencer.map((cuota, idx) => (
                      <div key={idx} className="flex items-center justify-between p-3 bg-amber-50 rounded-lg border border-amber-200">
                        <div>
                          <p className="font-medium text-gray-900">{cuota.concepto} - {cuota.periodo}</p>
                          <p className="text-sm text-amber-700">
                            Vence: {cuota.vencimiento} 
                            {cuota.dias_para_vencer !== null && (
                              <span className="ml-1">
                                ({cuota.dias_para_vencer === 0 ? 'Hoy' : `en ${cuota.dias_para_vencer} días`})
                              </span>
                            )}
                          </p>
                        </div>
                        <p className="font-bold text-amber-700">
                          ${cuota.importe.toLocaleString('es-AR', { timeZone: 'America/Argentina/Buenos_Aires', minimumFractionDigits: 2 })}
                        </p>
                      </div>
                    ))}
                  </div>
                </CardContent>
              )}
            </Card>
          )}

          {/* 7. CUOTAS DE CONVENIO PENDIENTES */}
          {estado.total_deuda_vencida > 0 && (
            <Card className="mb-4 border-red-500 bg-red-50" data-testid="deuda-total-real-card">
              <CardContent className="py-6">
                <p className="text-sm font-semibold text-red-800 mb-1 uppercase tracking-wide">
                  Deuda total <span className="text-xs normal-case font-normal">{leyendaDeudaTotal(estado)}</span>
                </p>
                <p className="text-3xl font-bold text-red-700" data-testid="deuda-total-real-monto">
                  ${((estado.total_deuda_vencida || 0) + (estado.total_pendiente || 0)).toLocaleString('es-AR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                </p>
              </CardContent>
            </Card>
          )}
          {cuotasConvenio.length > 0 && (
            <Card className="mb-4 border-purple-200">
              <CardHeader>
                <CardTitle className="text-lg flex items-center gap-2 text-purple-800">
                  <FileText className="w-5 h-5" />
                  Cuotas de Convenio Impagas
                  <Badge className="bg-purple-100 text-purple-700 ml-2">{cuotasConvenio.length} cuotas</Badge>
                </CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-sm text-gray-600 mb-4">
                  Cuotas impagas del convenio. Son deuda aunque ANTUAR les reasigne una fecha de vencimiento futura.
                </p>
                <div className="space-y-2">
                  {cuotasConvenio.map((cuota, idx) => (
                    <div key={idx} className="flex items-center justify-between p-3 bg-purple-50 rounded-lg border border-purple-200">
                      <div>
                        <p className="font-medium text-gray-900">{cuota.concepto}</p>
                        <p className="text-sm text-purple-700">
                          {cuota.periodo} - Vto. ANTUAR: {cuota.vencimiento}
                        </p>
                      </div>
                      <p className="font-bold text-purple-700">
                        ${cuota.importe.toLocaleString('es-AR', { timeZone: 'America/Argentina/Buenos_Aires', minimumFractionDigits: 2 })}
                      </p>
                    </div>
                  ))}
                </div>
                <div className="mt-4 p-3 bg-purple-100 rounded-lg">
                  <p className="text-sm text-purple-800 font-medium">
                    Total deuda de convenio: ${cuotasConvenio.reduce((sum, c) => sum + c.importe, 0).toLocaleString('es-AR', { timeZone: 'America/Argentina/Buenos_Aires', minimumFractionDigits: 2 })}
                  </p>
                </div>
              </CardContent>
            </Card>
          )}

          {/* 8. OPCIONES DE PAGO ANTICIPADO (con bonificación) */}
          {opcionesPago.length > 0 && (
            <Card className="mb-4">
              <CardHeader>
                <CardTitle className="text-lg">Opciones de pago anticipado (con bonificación)</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-2">
                  {opcionesPago.map((opcion, idx) => (
                    <div key={idx} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                      <div>
                        <p className="font-medium text-gray-900">
                          {opcion.periodo.includes('/AA') ? 'Pago Anual' : 
                           opcion.periodo.includes('/S1') ? 'Pago Semestral 1' :
                           opcion.periodo.includes('/S2') ? 'Pago Semestral 2' :
                           opcion.periodo}
                        </p>
                        <p className="text-sm text-gray-500">
                          Vence: {opcion.vencimiento}
                          {opcion.dias_para_vencer !== null && opcion.dias_para_vencer >= 0 && (
                            <span className="ml-1 text-green-600">
                              ({opcion.dias_para_vencer === 0 ? 'Hoy' : `${opcion.dias_para_vencer} días restantes`})
                            </span>
                          )}
                        </p>
                      </div>
                      <p className="font-semibold text-gray-700">
                        ${opcion.importe.toLocaleString('es-AR', { timeZone: 'America/Argentina/Buenos_Aires', minimumFractionDigits: 2 })}
                      </p>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          )}

          {/* Boleta de Pago Total con descuento - EN ROJO */}
          {estado.boleta_pago_total?.tiene_boleta && estado.boleta_pago_total?.vigente && (
            <Card className="mb-4 border-red-300 bg-red-50">
              <CardHeader>
                <CardTitle className="text-lg flex items-center gap-2 text-red-800">
                  <DollarSign className="w-5 h-5" />
                  Boleta de Pago Total
                  {boletaConDescuento(estado) && <Badge className="bg-red-600 text-white ml-2">80% desc. en mora</Badge>}
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="flex items-center justify-between p-4 bg-white rounded-lg border border-red-200">
                  <div>
                    <p className="font-medium text-gray-900">{boletaConDescuento(estado) ? "Pago total de deuda con descuento" : "Pago total de deuda"}</p>
                    {boletaConDescuento(estado) && (<p className="text-sm text-red-700 mt-1">
                      <strong>Incluye:</strong> Deuda anterior + mes en curso con 80% de descuento en intereses por mora
                    </p>)}
                    <p className="text-sm text-gray-500 flex items-center gap-1 mt-2">
                      <Calendar className="w-3 h-3" />
                      Vence: {estado.boleta_pago_total.vencimiento}
                    </p>
                  </div>
                  <div className="text-right">
                    <p className="text-2xl font-bold text-red-700">
                      ${estado.boleta_pago_total.importe.toLocaleString('es-AR', { timeZone: 'America/Argentina/Buenos_Aires', minimumFractionDigits: 2 })}
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>
          )}

          {/* PLANES DE PAGO */}
          {estado.planes_pago?.disponible && estado.planes_pago?.opciones?.length > 0 && (
            <Card className="mb-4 border-blue-200">
              <CardHeader 
                className="cursor-pointer hover:bg-blue-50 transition-colors"
                onClick={() => setPlanesPagoExpanded(!planesPagoExpanded)}
              >
                <div className="flex items-center justify-between">
                  <CardTitle className="text-lg flex items-center gap-2 text-blue-800">
                    <CreditCard className="w-5 h-5" />
                    Plan de Pagos - Opciones
                  </CardTitle>
                  {planesPagoExpanded ? (
                    <ChevronUp className="w-5 h-5 text-blue-600" />
                  ) : (
                    <ChevronDown className="w-5 h-5 text-blue-600" />
                  )}
                </div>
                <div className="mt-2 p-3 bg-blue-50 rounded-lg border border-blue-100">
                  <p className="text-sm font-medium text-blue-800 mb-2">
                    🔗 ¿Cómo gestionar el plan por autogestión?
                  </p>
                  <p className="text-sm text-gray-700 mb-2">
                    Podés hacerlo vos directamente ingresando al{' '}
                    <a 
                      href="https://www.antuar.com.ar/catspba" 
                      target="_blank" 
                      rel="noopener noreferrer" 
                      className="text-blue-600 underline font-medium"
                      onClick={(e) => e.stopPropagation()}
                    >
                      Portal ANTUAR
                    </a>{' '}
                    (te va a pedir N° Matrícula, DNI y Fecha de Nacimiento) &gt; Opción Plan de Pagos.
                  </p>
                  <p className="text-sm text-gray-600 italic">
                    Tené en cuenta que requiere adhesión con Tarjeta de Crédito VISA para el cobro de la cuota de moratoria + matrícula mensual.
                  </p>
                </div>
                <div className="mt-3 p-3 bg-green-50 rounded-lg border border-green-100">
                  <p className="text-sm font-medium text-green-800 mb-2">
                    📲 ¿Querés pagar por otros medios?
                  </p>
                  <p className="text-sm text-gray-700">
                    Si preferís gestionar un plan de pago abonando por transferencia, efectivo o Mercado Pago, solicitalo vía WhatsApp al{' '}
                    <a 
                      href="https://wa.me/541140989757" 
                      target="_blank" 
                      rel="noopener noreferrer" 
                      className="text-green-600 underline font-medium"
                      onClick={(e) => e.stopPropagation()}
                    >
                      11-4098-9757
                    </a>{' '}o por mail a{' '}
                    <a 
                      href="mailto:info@colegiotslomas.org" 
                      className="text-blue-600 underline font-medium"
                      onClick={(e) => e.stopPropagation()}
                    >
                      info@colegiotslomas.org
                    </a>
                  </p>
                </div>
                {estado.planes_pago.deuda_sin_descuento && (
                  <p className="text-sm text-gray-500 mt-1">
                    Deuda sin descuento: ${estado.planes_pago.deuda_sin_descuento.toLocaleString('es-AR', { timeZone: 'America/Argentina/Buenos_Aires', minimumFractionDigits: 2 })}
                  </p>
                )}
              </CardHeader>
              {planesPagoExpanded && (
                <CardContent>
                  <div className="space-y-2">
                    {estado.planes_pago.opciones.map((opcion, idx) => (
                      <div key={idx} className="flex items-center justify-between p-3 bg-blue-50 rounded-lg border border-blue-100">
                        <div>
                          <p className="font-medium text-gray-900">
                            {opcion.cantidad_cuotas} cuotas de ${opcion.importe_cuota.toLocaleString('es-AR', { timeZone: 'America/Argentina/Buenos_Aires', minimumFractionDigits: 2 })}
                          </p>
                          <p className="text-sm text-blue-700">
                            {opcion.descuento_mora} desc. en mora{opcion.tiene_interes ? ' • Con interés' : ' • Sin interés'}
                          </p>
                        </div>
                        <div className="text-right">
                          <p className="font-bold text-blue-800">
                            Total: ${opcion.total.toLocaleString('es-AR', { timeZone: 'America/Argentina/Buenos_Aires', minimumFractionDigits: 2 })}
                          </p>
                        </div>
                      </div>
                    ))}
                  </div>
                </CardContent>
              )}
            </Card>
          )}

          {/* Plan de pago vigente */}
          {estado.planes_pago?.tiene_plan_vigente && (
            <Card className="mb-4 border-green-200 bg-green-50">
              <CardContent className="py-4">
                <div className="flex items-start gap-3 text-green-800">
                  <CheckCircle className="w-5 h-5 mt-0.5 flex-shrink-0" />
                  <div>
                    <p className="font-medium">{estado.planes_pago.mensaje_plan_vigente || 'Ya tenés (o tuviste recientemente) un Plan de Pago vigente. Podés descargar el convenio desde la web de ANTUAR. Si creés que es un error, contactate con el Colegio.'}</p>
                    <div className="mt-3 p-3 bg-white rounded-lg border border-green-300">
                      <p className="text-sm text-gray-700 mb-2">
                        <strong>¿Necesitás gestionar un nuevo plan de pagos?</strong>
                      </p>
                      <p className="text-sm text-gray-600 mb-2">
                        Si el plan anterior quedó sin efecto o necesitás regularizar tu situación, podés comunicarte con el Distrito para gestionarlo de forma presencial.
                      </p>
                      <a 
                        href="https://wa.me/541140989757" 
                        target="_blank" 
                        rel="noopener noreferrer"
                        className="inline-flex items-center gap-2 text-green-700 hover:text-green-800 font-medium text-sm"
                      >
                        <MessageSquare className="w-4 h-4" />
                        Contactar por WhatsApp: 11-4098-9757
                        <ExternalLink className="w-3 h-3" />
                      </a>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          )}

          {/* Boleta vencida */}
          {estado.boleta_pago_total?.tiene_boleta && !estado.boleta_pago_total?.vigente && (
            <Card className="mb-4 border-gray-300 bg-gray-50">
              <CardContent className="py-4">
                <div className="flex items-center gap-3 text-gray-500">
                  <AlertCircle className="w-5 h-5" />
                  <p className="text-sm">
                    La oferta de pago total con descuento venció el {estado.boleta_pago_total.vencimiento}. 
                    Consultá en ANTUAR por nuevas opciones.
                  </p>
                </div>
              </CardContent>
            </Card>
          )}

          {/* LINK A ANTUAR */}
          <Card className="mb-4 bg-gray-50 border-gray-200">
            <CardContent className="py-4">
              <div className="flex items-start gap-3">
                <AlertCircle className="w-5 h-5 text-gray-600 mt-0.5 flex-shrink-0" />
                <div className="flex-1">
                  <p className="text-sm text-gray-800 font-medium mb-2">
                    Esta información es solo de consulta
                  </p>
                  <p className="text-sm text-gray-600 mb-3">
                    Para realizar pagos, gestionar planes de pago o descargar boletas, 
                    ingresá al portal oficial de ANTUAR.
                  </p>
                  
                  {/* Datos necesarios para ingresar a ANTUAR */}
                  <div className="bg-amber-100 border border-amber-300 rounded-lg p-3 mb-4">
                    <p className="text-sm font-semibold text-amber-800 mb-1">
                      ⚠️ Para ingresar a ANTUAR vas a necesitar:
                    </p>
                    <ul className="text-sm text-amber-700 list-disc list-inside space-y-1">
                      <li><strong>Número de matrícula</strong></li>
                      <li><strong>DNI</strong> (sin puntos)</li>
                      <li><strong>Fecha de nacimiento</strong> (no la fecha de hoy)</li>
                    </ul>
                  </div>
                  
                  <Button asChild className="bg-blue-600 hover:bg-blue-700">
                    <a href="https://www.antuar.com.ar/catspba/" target="_blank" rel="noopener noreferrer">
                      Ir a ANTUAR - Portal de Pagos
                      <ExternalLink className="w-4 h-4 ml-2" />
                    </a>
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>
        </>
      )}
    </div>
  );
}
